import React from "react";
import {
  Input,
  Button,
  Upload,
  Card,
  message,
  Tooltip,
  Tag,
  Radio,
} from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  UploadOutlined,
  VideoCameraOutlined,
  CheckCircleOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import {
  FAQ_IMAGE_UPLOAD_PATH,
  FAQ_IMAGE_PREVIEW_PATH,
} from "../../constant/comman";

const { TextArea } = Input;

export const isVimeoUrl = (url) => {
  if (!url || typeof url !== "string") return false;
  return /(?:vimeo\.com\/(?:(?:channels\/(?:\w+\/)?|groups\/[^\/]+\/videos\/|video\/)?))(\d+)/.test(url.trim());
};

export const getVimeoVideoId = (url) => {
  if (!url || typeof url !== "string") return "";
  const match = url
    .trim()
    .match(
      /(?:vimeo\.com\/(?:(?:channels\/(?:\w+\/)?|groups\/[^\/]+\/videos\/|video\/)?))(\d+)/
    );
  return match ? match[1] : "";
};

// Backward-compatible alias
export const isYouTubeUrl = isVimeoUrl;
export const getYouTubeVideoId = getVimeoVideoId;

export class FaqSectionBuilder extends React.Component {
  constructor(props) {
    super(props);
    const hasMultiple = props.sections && props.sections.length > 1;
    this.state = {
      isMultiStep: hasMultiple,
    };
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.sections !== this.props.sections &&
      this.props.sections &&
      this.props.sections.length > 1 &&
      !this.state.isMultiStep
    ) {
      this.setState({ isMultiStep: true });
    }
  }

  handleFormatChange = (e) => {
    const val = e.target.value;
    const isMulti = val === "multi";
    this.setState({ isMultiStep: isMulti });

    const { sections, onChange } = this.props;
    if (!isMulti && sections && sections.length > 1) {
      // Switched to single: retain only the first section
      onChange([sections[0]]);
    } else if (isMulti && (!sections || sections.length === 0)) {
      onChange([
        {
          description: "",
          image: "",
          video_url: "",
          order: 1,
        },
      ]);
    }
  };

  addSection = () => {
    const { sections, onChange } = this.props;
    const newSections = [
      ...(sections || []),
      {
        description: "",
        image: "",
        video_url: "",
        order: (sections ? sections.length : 0) + 1,
      },
    ];
    onChange(newSections);
  };

  removeSection = (index) => {
    const { sections, onChange } = this.props;
    const newSections = sections.filter((_, i) => i !== index);
    const reindexed = newSections.map((sec, idx) => ({
      ...sec,
      order: idx + 1,
    }));
    onChange(reindexed);
  };

  updateSection = (index, field, value) => {
    const { sections, onChange } = this.props;
    const newSections = (sections || [{}]).map((sec, i) => {
      if (i === index) {
        return { ...sec, [field]: value };
      }
      return sec;
    });
    onChange(newSections);
  };

  handleUploadChange = (index, info) => {
    if (info.file.status === "done") {
      const uploadedFileName =
        info.file.response && info.file.response.file
          ? info.file.response.file
          : null;
      if (uploadedFileName) {
        message.success(`${info.file.name} uploaded successfully`);
        this.updateSection(index, "image", uploadedFileName);
      } else {
        message.error("Failed to save image on server");
      }
    } else if (info.file.status === "error") {
      message.error(`${info.file.name} upload failed`);
    }
  };

  renderSectionCard = (sec, index, isMultiStep, totalSections) => {
    const vimeoId = sec.video_url ? getVimeoVideoId(sec.video_url) : null;
    const isValidVimeo = sec.video_url ? isVimeoUrl(sec.video_url) : true;
    const isSectionValid =
      (sec.description && sec.description.trim()) ||
      sec.image ||
      (sec.video_url && isValidVimeo);

    return (
      <Card
        key={index}
        size="small"
        style={{
          marginBottom: "14px",
          borderRadius: "8px",
          border: "1px solid #e2e8f0",
          boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
          backgroundColor: "#fafbfc",
        }}
        title={
          isMultiStep ? (
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <Tag color="#1890ff" style={{ borderRadius: "12px", padding: "0 10px" }}>
                Step {index + 1}
              </Tag>
              <span style={{ fontSize: "12px", color: "#64748b" }}>
                (Fill description, image, or Vimeo video)
              </span>
            </div>
          ) : (
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span style={{ fontWeight: 600, color: "#1e293b", fontSize: "13px" }}>
                Answer Content
              </span>
              <span style={{ fontSize: "12px", color: "#64748b" }}>
                (Fill text, image, or Vimeo video — at least one is required)
              </span>
            </div>
          )
        }
        extra={
          isMultiStep && totalSections > 1 ? (
            <Tooltip title="Delete Step">
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={() => this.removeSection(index)}
              />
            </Tooltip>
          ) : null
        }
      >
        {/* Description */}
        <div style={{ marginBottom: "12px" }}>
          <label style={{ fontSize: "12px", fontWeight: 500, color: "#475569" }}>
            {isMultiStep ? `Step ${index + 1} Description / Text` : "Answer Text / Description"}
          </label>
          <TextArea
            rows={3}
            placeholder={
              isMultiStep
                ? `Explain Step ${index + 1}...`
                : "Type the answer text here..."
            }
            value={sec.description || ""}
            onChange={(e) =>
              this.updateSection(index, "description", e.target.value)
            }
            style={{ marginTop: "4px", borderRadius: "6px" }}
          />
        </div>

        {/* Image Upload */}
        <div style={{ marginBottom: "12px" }}>
          <label style={{ fontSize: "12px", fontWeight: 500, color: "#475569" }}>
            Screenshot / Image (Optional)
          </label>
          {sec.image ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                marginTop: "6px",
                padding: "8px 12px",
                backgroundColor: "#fff",
                borderRadius: "6px",
                border: "1px solid #e2e8f0",
              }}
            >
              <img
                src={`${FAQ_IMAGE_PREVIEW_PATH}${sec.image}`}
                alt="preview"
                style={{
                  width: "80px",
                  height: "50px",
                  objectFit: "cover",
                  borderRadius: "4px",
                  border: "1px solid #cbd5e1",
                }}
              />
              <div style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis" }}>
                <div style={{ fontSize: "12px", fontWeight: 500, color: "#334155" }}>
                  {sec.image}
                </div>
                <a
                  href={`${FAQ_IMAGE_PREVIEW_PATH}${sec.image}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ fontSize: "11px", color: "#1890ff" }}
                >
                  View Full Image
                </a>
              </div>
              <Button
                type="text"
                danger
                icon={<CloseOutlined />}
                size="small"
                onClick={() => this.updateSection(index, "image", "")}
              >
                Remove
              </Button>
            </div>
          ) : (
            <div style={{ marginTop: "4px" }}>
              <Upload
                name="file"
                action={FAQ_IMAGE_UPLOAD_PATH}
                showUploadList={false}
                onChange={(info) => this.handleUploadChange(index, info)}
              >
                <Button icon={<UploadOutlined />} style={{ borderRadius: "6px" }}>
                  {isMultiStep ? `Upload Step ${index + 1} Image` : "Upload Image / Screenshot"}
                </Button>
              </Upload>
            </div>
          )}
        </div>

        {/* Vimeo Video URL */}
        <div>
          <label style={{ fontSize: "12px", fontWeight: 500, color: "#475569" }}>
            Vimeo Video URL (Optional)
          </label>
          <Input
            prefix={<VideoCameraOutlined style={{ color: "#1ab7ea" }} />}
            placeholder="https://vimeo.com/123456789 or https://player.vimeo.com/video/123456789"
            value={sec.video_url || ""}
            onChange={(e) =>
              this.updateSection(index, "video_url", e.target.value)
            }
            style={{
              marginTop: "4px",
              borderRadius: "6px",
              borderColor:
                sec.video_url && !isValidVimeo ? "#ef4444" : undefined,
            }}
          />

          {sec.video_url && (
            <div style={{ marginTop: "6px" }}>
              {isValidVimeo && vimeoId ? (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    padding: "6px 10px",
                    backgroundColor: "#f0f9ff",
                    borderRadius: "6px",
                    border: "1px solid #bae6fd",
                  }}
                >
                  <Tag color="processing" icon={<CheckCircleOutlined />}>
                    Valid Vimeo Video (ID: {vimeoId})
                  </Tag>
                  <a
                    href={sec.video_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ fontSize: "11px", marginLeft: "6px", color: "#0284c7" }}
                  >
                    Open on Vimeo
                  </a>
                </div>
              ) : (
                <div style={{ color: "#ef4444", fontSize: "12px" }}>
                  Only Vimeo URLs are accepted (e.g. https://vimeo.com/123456789 or https://player.vimeo.com/video/123456789)
                </div>
              )}
            </div>
          )}
        </div>

        {!isSectionValid && (
          <div style={{ color: "#f59e0b", fontSize: "11px", marginTop: "8px" }}>
            Please provide at least a Description, an Image, or a Vimeo video.
          </div>
        )}
      </Card>
    );
  };

  render() {
    const { sections } = this.props;
    const { isMultiStep } = this.state;
    const sectionList =
      sections && sections.length > 0
        ? sections
        : [
            {
              description: "",
              image: "",
              video_url: "",
              order: 1,
            },
          ];

    return (
      <div style={{ marginTop: "16px" }}>
        {/* Toggle Format: Single Step vs Multi Steps */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "16px",
            padding: "10px 14px",
            backgroundColor: "#f1f5f9",
            borderRadius: "8px",
            border: "1px solid #e2e8f0",
          }}
        >
          <div>
            <div style={{ fontWeight: 600, fontSize: "13px", color: "#334155" }}>
              Answer Format
            </div>
            <div style={{ fontSize: "12px", color: "#64748b" }}>
              {isMultiStep
                ? "Multi-step tutorial with sequential steps (Step 1, Step 2...)"
                : "Single answer with optional image or video"}
            </div>
          </div>

          <Radio.Group
            value={isMultiStep ? "multi" : "single"}
            onChange={this.handleFormatChange}
            buttonStyle="solid"
          >
            <Radio.Button value="single">Single Step</Radio.Button>
            <Radio.Button value="multi">Multi Steps</Radio.Button>
          </Radio.Group>
        </div>

        {/* Multi-step Header with Add button */}
        {isMultiStep && (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "12px",
            }}
          >
            <div style={{ fontWeight: 600, fontSize: "14px", color: "#333" }}>
              Tutorial Steps ({sectionList.length})
            </div>
            <Button
              type="dashed"
              icon={<PlusOutlined />}
              onClick={this.addSection}
              style={{ borderRadius: "6px" }}
            >
              Add Step
            </Button>
          </div>
        )}

        {/* Cards rendering */}
        {isMultiStep ? (
          sectionList.map((sec, idx) =>
            this.renderSectionCard(sec, idx, true, sectionList.length)
          )
        ) : (
          this.renderSectionCard(sectionList[0] || {}, 0, false, 1)
        )}
      </div>
    );
  }
}
